import type { Note, NoteGroup } from '../../types';
import { GROUP_COLORS } from './groupColors';
import { expiryBadge, formatDate, isArchiveGroup } from './noteUtils';
import LinkifiedText from '../LinkifiedText';

interface NoteCardProps {
  note: Note;
  group?: NoteGroup;
  onView: (note: Note) => void;
  onEdit: (note: Note) => void;
  onDelete: (note: Note) => void;
  onArchive: (note: Note) => void;
}

export default function NoteCard({
  note,
  group,
  onView,
  onEdit,
  onDelete,
  onArchive,
}: NoteCardProps) {
  const badge = expiryBadge(note.expires_at);
  const groupStyle = group
    ? GROUP_COLORS[group.color] ?? GROUP_COLORS.primary
    : null;
  const isArchived = group ? isArchiveGroup(group.name) : false;

  return (
    <div
      className="group flex cursor-pointer flex-col rounded-xl border-2 border-orange-500 bg-background-50 p-4 transition hover:border-orange-600 sm:rounded-lg"
      onClick={() => onView(note)}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3 className="line-clamp-2 font-heading text-base font-semibold text-foreground-900">
          {note.title}
        </h3>
        {note.telegram_alert && (
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700"
            title="Bật cảnh báo Telegram"
          >
            <i className="ri-send-plane-2-line text-sm" />
          </span>
        )}
      </div>

      <p className="mb-3 line-clamp-4 flex-1 text-sm leading-relaxed text-foreground-500">
        {note.content ? (
          <LinkifiedText text={note.content} />
        ) : (
          'Không có nội dung'
        )}
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {group && groupStyle && (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${groupStyle.badge}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${groupStyle.dot}`} />
            {group.name}
          </span>
        )}
        {badge && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${badge.className}`}
          >
            <i
              className={`${
                badge.label === 'Sắp hết hạn' || badge.label === 'Đã hết hạn'
                  ? 'ri-alarm-warning-line'
                  : 'ri-calendar-line'
              } text-xs`}
            />
            {badge.label}
          </span>
        )}
        {note.attachments?.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-500">
            <i className="ri-attachment-2 text-xs" />
            {note.attachments.length}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between border-t-2 border-orange-500/60 pt-3">
        <span className="text-xs text-foreground-400">
          {formatDate(note.updated_at)}
        </span>
        <div className="flex items-center gap-1">
          {!isArchived && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onArchive(note);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-400 transition hover:bg-secondary-100 hover:text-secondary-700 sm:h-8 sm:w-8"
              aria-label="Lưu trữ"
              title="Chuyển vào Lưu trữ"
            >
              <i className="ri-inbox-archive-line text-lg" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(note);
            }}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-400 transition hover:bg-background-100 hover:text-foreground-700 sm:h-8 sm:w-8"
            aria-label="Chỉnh sửa"
          >
            <i className="ri-edit-line text-lg" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(note);
            }}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-400 transition hover:bg-accent-100 hover:text-accent-700 sm:h-8 sm:w-8"
            aria-label="Xóa"
          >
            <i className="ri-delete-bin-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}